import { AbstractControl } from '@angular/forms'
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, Inject, Input, OnInit, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NgControl } from '@angular/forms'
import { startWith } from 'rxjs'
import { E11DynamicFormModule, IE11DynamicComponent } from '@engineering11/ui-lib/e11-form-builder'
import { E11InputErrorsModule } from '@engineering11/ui-lib/e11-input-errors'
import { TranslateModule } from '@ngx-translate/core'
import { C_JOB_CATEGORY_PATTERN_MAP, CJobCategory, CJobPattern, C_JOB_PATTERN_CATEGORY_MAP, IApprovedCJobPattern } from '../c-job/c-job.model'
import { PatternTraitPillComponent } from './pattern-trait-pill.component'
import { PatternTraitsListWrapperComponent } from './pattern-traits-list-wrapper.component'

const DISPLAY_CATEGORIES: CJobCategory[] = [CJobCategory.Visionary, CJobCategory.Research, CJobCategory.Social, CJobCategory.Organizational]

function groupApprovedPatternsByCategory(approved: IApprovedCJobPattern[]): Map<CJobCategory, IApprovedCJobPattern[]> {
  const byCategory = new Map<CJobCategory, IApprovedCJobPattern[]>()
  for (const cat of DISPLAY_CATEGORIES) {
    byCategory.set(cat, [])
  }
  for (const p of approved) {
    const cat = C_JOB_PATTERN_CATEGORY_MAP[p.patternName as CJobPattern]?.category ?? CJobCategory.Other
    if (byCategory.has(cat)) {
      byCategory.get(cat)!.push(p)
    }
  }
  return byCategory
}

@Component({
  selector: 'pattern-traits-select-input',
  imports: [PatternTraitPillComponent, PatternTraitsListWrapperComponent, TranslateModule, E11DynamicFormModule, E11InputErrorsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  template: `
    <div class="e11-w-full e11-flex e11-flex-col e11-gap-2">
      @if (approvedPatterns.length && categoryConfigMap()) { @for (cat of DISPLAY_CATEGORIES; track cat) { @let categoryConfig =
      categoryConfigMap()![cat]; @if (categoryConfig && categoryConfig.patterns.length) {
      <pattern-traits-list-wrapper [iconColor]="categoryConfig.secondaryTextColor" [tooltipText]="categoryConfig.category | translate">
        @for (pattern of categoryConfig.patterns; track pattern.id) {
        <pattern-trait-pill
          [patternName]="pattern.patternName"
          [isChecked]="selectedIds().includes(pattern.id.toString())"
          (onSelect)="onSelectPattern(pattern)"
        ></pattern-trait-pill>
        }
      </pattern-traits-list-wrapper>
      } } } @if (!approvedPatterns.length) { @for (cat of DISPLAY_CATEGORIES; track cat) { @let categoryConfig = C_JOB_CATEGORY_PATTERN_MAP[cat];
      <pattern-traits-list-wrapper [iconColor]="categoryConfig.secondaryTextColor" [tooltipText]="categoryConfig.category | translate">
        @for (pattern of categoryConfig.patterns; track pattern) {
        <pattern-trait-pill
          [patternName]="pattern"
          [isChecked]="selectedNames().includes(pattern)"
          (onSelect)="onSelectPatternName(pattern)"
        ></pattern-trait-pill>
        }
      </pattern-traits-list-wrapper>
      } } @if (effectiveControl(); as ctrl) {
      <e11-input-errors [formControl]="ctrl"></e11-input-errors>
      }
    </div>
  `,
})
export class PatternTraitsSelectInputComponent implements IE11DynamicComponent, OnInit {
  @Input() approvedPatterns: IApprovedCJobPattern[] = []

  @Input() loading = false
  selectedIds = signal<string[]>([])
  selectedNames = signal<string[]>([])

  value: string[] = []

  onChange: (v: string[]) => void = () => {}
  onTouch: () => void = () => {}

  private destroyRef = inject(DestroyRef)

  DISPLAY_CATEGORIES = DISPLAY_CATEGORIES
  C_JOB_CATEGORY_PATTERN_MAP = C_JOB_CATEGORY_PATTERN_MAP

  effectiveControl = computed(() => this.ngControl?.control)

  categoryConfigMap = computed(() => {
    const approved = this.approvedPatterns
    if (!approved.length) return null
    const grouped = groupApprovedPatternsByCategory(approved)
    const map: Partial<
      Record<
        CJobCategory,
        { category: CJobCategory; patterns: IApprovedCJobPattern[] } & Omit<(typeof C_JOB_CATEGORY_PATTERN_MAP)[CJobCategory], 'patterns'>
      >
    > = {}
    for (const cat of DISPLAY_CATEGORIES) {
      const patterns = grouped.get(cat) ?? []
      const base = C_JOB_CATEGORY_PATTERN_MAP[cat]
      if (patterns.length && base) {
        const { patterns: _p, ...rest } = base
        map[cat] = { ...rest, category: cat, patterns }
      }
    }
    return map
  })

  constructor(@Inject(NgControl) public ngControl: NgControl | null, private cdr: ChangeDetectorRef) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this
    }
  }

  ngOnInit() {
    this.subscribeToControl(this.ngControl?.control)
  }

  private subscribeToControl(control: AbstractControl | null | undefined) {
    if (!control) return
    control.statusChanges.pipe(startWith(control.status), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck())
  }

  registerOnChange(fn: (v: string[]) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn
  }

  writeValue(value: string[] | null): void {
    const ids = value ?? []
    if (this.approvedPatterns.length) {
      this.selectedIds.set(ids)
    } else {
      this.selectedNames.set(ids)
    }
    this.value = ids
  }

  onSelectPattern(pattern: IApprovedCJobPattern) {
    const id = pattern.id.toString()
    this.selectedIds.update(prev => (prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]))
    const next = this.selectedIds()
    this.onChange(next)
    this.onTouch()
  }

  onSelectPatternName(patternName: string) {
    this.selectedNames.update(prev => (prev.includes(patternName) ? prev.filter(v => v !== patternName) : [...prev, patternName]))
    const next = this.selectedNames()
    this.onChange(next)
    this.onTouch()
  }
}
